import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
} from "@mui/material";
import {
  ArrowForward,
  Article,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { blogService } from "../../services/api";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://backendapi.vinautochecker.com";

const PostList = ({ isDashboard = false, searchTerm = "" }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  useEffect(() => {
    fetchPosts();
  }, [page, searchTerm]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError("");

      let response;

      if (isDashboard) {
        response = await blogService.getUserPosts();
      } else {
        response = await blogService.getAllPosts({
          page: page,
          search: searchTerm || undefined,
        });
      }

      const api = response.data;
      const server = api.data;

      let postsData = [];
      if (server && Array.isArray(server.data)) {
        postsData = server.data;
      }

      setPosts(postsData);
      setTotalPages(server.last_page || 1);
      setTotalPosts(server.total || postsData.length);
      setPage(server.current_page || 1);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError("Failed to load posts. Please try again later.");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3, mx: "auto", maxWidth: 600 }}>
        <Typography variant="body1">{error}</Typography>
        <Button
          variant="outlined"
          onClick={fetchPosts}
          sx={{ mt: 1 }}
          size="small"
        >
          Retry
        </Button>
      </Alert>
    );
  }

  if (posts.length === 0) {
    return (
      <Box
        textAlign="center"
        py={8}
        sx={{
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.default",
        }}
      >
        <Article sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {isDashboard
            ? "You haven't created any posts yet."
            : searchTerm
            ? `No posts found for "${searchTerm}"`
            : "No posts available yet."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {!isDashboard && (
        <Typography variant="body2" color="text.secondary" mb={3}>
          Showing {posts.length} of {totalPosts} posts
        </Typography>
      )}

      {/* POSTS GRID */}
      <Grid container spacing={3}>
        {posts.map((post) => (
          <Grid item xs={12} md={12} key={post.id}>
            <Card
              sx={{
                display: "flex",
                flexDirection: "row",
                height: 250,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 3,
                transition: ".3s",
                "&:hover": {
                  boxShadow: 8,
                  transform: "translateY(-4px)",
                },
              }}
            >
              {/* LEFT SIDE IMAGE */}
              {post.featured_image ? (
                <CardMedia
                  component="img"
                  image={`${API_BASE_URL}/${post.featured_image.replace(
                    /^\//,
                    ""
                  )}`}
                  alt={post.title}
                  sx={{
                    width: 300,
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 300,
                    height: "100%",
                    bgcolor: "grey.200",
                  }}
                />
              )}

              {/* RIGHT SIDE CONTENT */}
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 3,
                }}
              >
                <Chip
                  label={post.category || "Uncategorized"}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1, width: "fit-content" }}
                />

                <Typography
                  variant="h6"
                  component={RouterLink}
                  to={`/post/${post.id}`}
                  sx={{
                    textDecoration: "none",
                    color: "text.primary",
                    fontWeight: 700,
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {post.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    mb: 2,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {post.meta_description ||
                    post.excerpt ||
                    (post.content
                      ? post.content.substring(0, 150) + "..."
                      : "")}
                </Typography>

                {/* META + READ BUTTON */}
                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {post.user?.name || "Anonymous"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {new Date(post.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    component={RouterLink}
                    to={`/post/${post.id}`}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    Read
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* PAGINATION */}
      {!isDashboard && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={6}>
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: 2,
                  fontWeight: 500,
                },
              }}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PostList;
